import { Box, VBox } from "@/components-v2/Box";
import { CloseButton, IconButton } from "@/components-v2/Button";
import { ColumnSelectorButton, ColumnSelectorDialog } from "@/components-v2/ColumnSelectorDialog";
import { DataTable } from "@/components-v2/DataTable";
import { DateInput } from "@/components-v2/DateInput";
import { prepDateInput } from "@/components-v2/dateInputDomain";
import { FilterButton, FilterDialog } from "@/components-v2/FilterDialog";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { ResizableSidepanelLayout } from "@/components-v2/ResizableSidepanelLayout";
import { TextInput } from "@/components-v2/TextInput";
import { navigateTo } from "@/events/eventsDomain";
import { DetailsFields } from "@/shareholderRegister/shareholders/shareholderDetailsComponents";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { useDispatch, useTranslation } from "@/stores/RootStoreContext";
import { preventDefault } from "@/utils/events";
import { when } from "@/utils/functions";
import { observer } from "mobx-react";
import { FC } from "react";
import { ReportRow } from "../search/portfolioV2ApiClient";
import {
  closeDetailsPanel,
  closeReportsColumnSelector,
  closeReportsFilterDialog,
  PortfolioReportsState,
  prepReportRowDetailsFields,
  prepReportsColumnSelectorButton,
  prepReportsFilterButton,
  prepReportsMenu,
  prepReportsTable,
  reportsColumnLabel,
  setDate,
  setDateAndSubmit,
  submitReportsColumnSelector,
  submitReportsFilterDialog,
  submitReportSearch,
  updateQuery,
  updateReportsColumnSelector,
  updateReportsFilterDialog,
} from "./reportsDomain";
import noop from "lodash/noop";
import { LoadingOverlay } from "@/components-v2/LoadingOverlay";

const ReportsRowDetailsPanel = observer(
  ({ state, row }: { state: PortfolioReportsState; row: ReportRow }) => {
    const { t } = useTranslation();
    return (
      <VBox grow overflow={"hidden"}>
        <Box padding alignItems={"center"} justifyContent={"space-between"}>
          <h4>{t("details")}</h4>
          <CloseButton onClick={[closeDetailsPanel]} />
        </Box>
        <VBox grow overflow={"auto"} paddingX paddingBottom>
          <DetailsFields fields={prepReportRowDetailsFields(state, row)} />
        </VBox>
      </VBox>
    );
  },
);

const ReportsResultPanel: FC<{ state: PortfolioReportsState }> = observer(({ state }) => {
  const dispatch = useDispatch();

  return (
    <VBox grow overflow={"hidden"} gap={false}>
      <Box
        padding
        element={"form"}
        alignItems={"center"}
        htmlProps={{
          onSubmit: preventDefault(() => dispatch(submitReportSearch(state))),
        }}
      >
        <input type="submit" className="hidden" />
        <TextInput
          name={"query"}
          value={state.query ?? ""}
          onChange={(event) => updateQuery(event.target.value)}
          icon={"magnifying-glass"}
          placeholder={{ key: "portfolio.reports.query.placeholder" }}
          inline
        />
        <DateInput
          {...prepDateInput({
            state: state.date,
            onInput: setDate,
            onChange: setDateAndSubmit,
          })}
          placeholder={{ key: "portfolio.reports.date.placeholder" }}
          inline
          messageVariant={"tooltip"}
        />
        <ColumnSelectorButton {...prepReportsColumnSelectorButton(state)} />
        <FilterButton {...prepReportsFilterButton(state)} />
      </Box>
      <VBox grow overflow={"hidden"}>
        {when(prepReportsTable(state), (props) => (
          <DataTable className="mx-md" variant={"IN_CARD"} stickyHeader {...props} />
        ))}
        {state.isLoading && <LoadingOverlay />}
      </VBox>
      {when(state.columnSelector, (columnSelector) => (
        <ColumnSelectorDialog
          state={columnSelector}
          getColumnLabel={(name) => reportsColumnLabel(state, name)}
          update={updateReportsColumnSelector}
          onSubmit={submitReportsColumnSelector}
          onCancel={closeReportsColumnSelector}
        />
      ))}
      {when(state.filterDialog, (filterDialog) => (
        <FilterDialog
          state={filterDialog}
          update={updateReportsFilterDialog}
          onSubmit={submitReportsFilterDialog}
          onCancel={closeReportsFilterDialog}
        />
      ))}
    </VBox>
  );
});

const ReportTitle = observer(() => {
  const { t } = useTranslation();
  return (
    <Box alignItems={"center"}>
      <span>{t("portfolio.reports.new-report-title")}</span>
      <IconButton icon={"edit"} disabled onClick={noop} tooltip={{ key: "edit" }} />
    </Box>
  );
});

type ReportsViewProps = {
  state: PortfolioReportsState | undefined;
  alertStore: AlertMessageStore;
};

export const ReportsView: FC<ReportsViewProps> = observer(({ state, alertStore }) => {
  return (
    <FullScreenLayout
      bg
      appBar={false}
      alertStore={alertStore}
      pageHeader={
        state && {
          onGoBack: [navigateTo({ name: "portfolio/reports" })],
          onGoBackVariant: "x",
          title: <ReportTitle />,
          menu: prepReportsMenu(state),
          containerProps: { borderBottom: false },
        }
      }
      centerPanel={
        !state ? (
          "loader"
        ) : (
          <ResizableSidepanelLayout
            containerProps={{ overflow: "hidden", grow: true }}
            centerPanel={<ReportsResultPanel state={state} />}
            rightBaseSizePercent={30}
            rightPanel={when(state.selectedRow, (row) => (
              <ReportsRowDetailsPanel state={state} row={row} />
            ))}
          />
        )
      }
    />
  );
});
